import React from 'react'
import styled from 'styled-components'

import Button from '@/components/button'
import {Text} from '@/components/text'
import {Video} from '@/components/video'
import {BackgroundWrapper} from '@/styles/grid'
import {radii, tokens} from '@/theme'
import mq from '@/utils/media-query'

import type {ImageSource} from '@/types/image'
import type {InlineVideo} from '@/types/inline-video'
import type {BackgroundColor} from '@/utils/is-valid-background-color'

export interface ImageCardProps {
    headline: string
    description: string
    backgroundColor: BackgroundColor
    CTA?: React.ComponentType<any>
    Image?: React.ComponentType<any>
    videoPoster?: ImageSource
    video?: InlineVideo
}

export const ImageCard = React.forwardRef<HTMLDivElement, ImageCardProps>(
    (
        {
            headline,
            description,
            backgroundColor = 'purple',
            CTA,
            Image,
            video,
            videoPoster,
            ...rest
        },
        ref,
    ) => (
        <Wrapper $color={backgroundColor} {...rest} ref={ref}>
            <CopyWrapper>
                <Text $variant="heading-base">{headline}</Text>
                <Description $variant="paragraph-large">{description}</Description>
                {CTA && <StyledButton forwardedAs={CTA} />}
            </CopyWrapper>
            {video && (
                <StyledVideo
                    poster={videoPoster?.url}
                    video={video}
                    dimensions={videoPoster?.dimensions}
                />
            )}
            {Image && !video && <StyledImage as={Image} />}
        </Wrapper>
    ),
)

const Wrapper = styled(BackgroundWrapper)`
    border-radius: ${radii.arc20};
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;

    ${mq.min('md')} {
        flex-direction: row-reverse;
    }
`

const CopyWrapper = styled.div`
    display: flex;
    flex-direction: column;
    padding: ${tokens.spacing20};

    ${mq.min('sm')} {
        padding: ${tokens.spacing40};
    }

    ${mq.min('md')} {
        width: 50%;
        justify-content: center;
    }
`

const StyledButton = styled(Button)`
    ${mq.min('sm')} {
        align-self: flex-start;
    }
`

const StyledImage = styled.img`
    width: 100%;

    ${mq.min('md')} {
        width: 50%;
    }
`

const StyledVideo = styled(Video)`
    width: 100%;

    ${mq.min('md')} {
        width: 50%;
    }
`

const Description = styled(Text)`
    margin-top: ${tokens.spacing12};
    margin-bottom: ${tokens.spacing40};

    ${mq.min('md')} {
        margin-top: ${tokens.spacing20};
    }
`
